/**
 * ч.1357: сторінка статзвіту до Держстату — бланк 1-опт (товарні групи × графи) або
 * 10-зез (показники × країни-нерезиденти, розділ III, запитання), реквізити бланка,
 * ознака «даних немає», перевірка контролями Держстату і проти XSD, файл XML,
 * підпис і відправка у шлюз Держстату, квитанції.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FORM_BY_SLUG, FORM_SHORT, FORM_TITLE, QUARTER_LABELS, optTotals, statReports,
  type GoodsHit, type OptControl, type OptRow, type StatCheck, type StatCountryBlock, type StatFields,
  type StatFormSlug, type StatSubmission, type ZezRow,
} from '@/api/statReports';
import { statSubmissions } from '@/api/statReports';
import { orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import KatottgPicker from '@/shared/components/KatottgPicker';
import TchTable from '@/shared/components/TchTable';
import StatSignPanel from './StatSignPanel';

const COL_SHORT: Record<number, string> = {
  1: 'Обсяг продажу (натур.)', 2: 'Товарооборот без ПДВ, тис.грн', 3: 'із гр.2 — іншим оптовикам',
  4: 'із гр.2 — вироблені в Україні', 5: 'Запаси (натур.) на кінець кв.', 6: 'Запаси без ПДВ, тис.грн',
};

function NumInput({ value, onChange, disabled, title, label }: {
  value: string; onChange: (v: string) => void; disabled?: boolean; title?: string; label?: string;
}) {
  return (
    <input type="text" inputMode="decimal" value={value} disabled={disabled} title={title} aria-label={label}
      onChange={(e) => onChange(e.target.value.replace(/[^\d.,-]/g, ''))}
      style={{ width: '100%', textAlign: 'right' }} />
  );
}

export default function StatReportEditor({ form }: { form: StatFormSlug }) {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const formCode = FORM_BY_SLUG[form];
  const [values, setValues] = useState<Record<string, string>>({});
  const [countries, setCountries] = useState<StatCountryBlock[]>([]);
  const [fields, setFields] = useState<StatFields>({});
  const [docCnt, setDocCnt] = useState('1');
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [check, setCheck] = useState<StatCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [goodsQ, setGoodsQ] = useState('');
  const [goods, setGoods] = useState<GoodsHit[]>([]);
  const [highlight, setHighlight] = useState<string | null>(null);
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

  const docQ = useQuery({ queryKey: ['stat-report', id], queryFn: () => statReports.get(id!), enabled: !!id });
  const d = docQ.data;
  const defQ = useQuery({ queryKey: ['stat-form', formCode], queryFn: () => statReports.form(formCode), staleTime: 3_600_000 });
  const def = defQ.data;
  const subQ = useQuery({ queryKey: ['stat-report-sub', id], queryFn: () => statSubmissions.list({ report_id: id!, limit: 1 }), enabled: !!id, refetchInterval: 30_000 });
  const sub: StatSubmission | null = subQ.data?.items[0] ?? null;
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgListQ.data?.items.find((o) => o.id === d?.organization_id);

  useEffect(() => {
    if (d) { setValues(d.values ?? {}); setCountries(d.countries ?? []); setFields(d.fields ?? {}); setDocCnt(String(d.doc_cnt ?? 1)); setDirty(false); }
  }, [d]);
  useEffect(() => {
    if (goodsQ.trim().length < 2) { setGoods([]); return; }
    const t = setTimeout(() => statReports.goods(goodsQ.trim()).then((r) => setGoods(r.items)).catch(() => setGoods([])), 250);
    return () => clearTimeout(t);
  }, [goodsQ]);

  const subStatus = sub?.status ?? null;
  const readOnly = !!subStatus && ['SENT', 'DELIVERED', 'ACCEPTED'].includes(subStatus);
  const onErr = (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e));
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['stat-report', id] }); qc.invalidateQueries({ queryKey: ['stat-report-sub', id] }); qc.invalidateQueries({ queryKey: ['stat-subs-all'] }); };

  const save = useMutation({
    mutationFn: () => {
      const n = Number(docCnt);
      if (!Number.isInteger(n) || n < 1 || n > 999) throw new Error('Номер звіту в періоді — ціле число від 1 до 999');
      return statReports.update(id!, { values, countries, fields, doc_cnt: n });
    },
    onSuccess: () => { setDirty(false); setError(null); setCheck(null); invalidate(); },
    onError: onErr,
  });
  const remove = useMutation({
    mutationFn: () => statReports.remove(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['stat-subs-all'] }); navigate(`/stat-reports/${form}`); },
    onError: onErr,
  });
  const saveDocDate = useMutation({
    mutationFn: (ymd: string) => statReports.update(id!, { document_date: `${ymd}T00:00:00Z` }),
    onSuccess: invalidate, onError: onErr,
  });

  async function runCheck() {
    if (!id) return;
    setChecking(true);
    try { if (dirty && !readOnly) await save.mutateAsync(); setCheck(await statReports.check(id)); }
    catch (e: any) { onErr(e); } finally { setChecking(false); }
  }
  async function getFiles() {
    if (dirty && !readOnly) await save.mutateAsync();
    const r = await statReports.files(id!);
    return { ...r.files[0]!, sender_email: r.sender_email };
  }
  const setV = (code: string, raw: string) => { setValues((p) => { const n = { ...p }; if (raw === '') delete n[code]; else n[code] = raw; return n; }); setDirty(true); };
  const setF = (patch: Partial<StatFields>) => { setFields((p) => ({ ...p, ...patch })); setDirty(true); };
  const setCountry = (i: number, patch: Partial<StatCountryBlock>) => { setCountries((p) => p.map((c, k) => (k === i ? { ...c, ...patch } : c))); setDirty(true); };
  const setCV = (i: number, row: string, raw: string) => {
    setCountries((p) => p.map((c, k) => {
      if (k !== i) return c;
      const vals = { ...c.values }; if (raw === '') delete vals[row]; else vals[row] = raw;
      return { ...c, values: vals };
    }));
    setDirty(true);
  };
  function totals() {
    if (!def?.controls) return;
    setValues((p) => optTotals(p, def.controls as OptControl[]));
    setDirty(true);
  }
  function jumpTo(row: string) {
    setHighlight(row); setGoods([]); setGoodsQ('');
    const el = rowRefs.current[row];
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el?.querySelector('input')?.focus();
  }

  if (!d || !def) return <div className="card">Завантаження…</div>;
  const title = `${FORM_SHORT[formCode]} — ${QUARTER_LABELS[d.period_quarter]} ${d.period_year}${d.doc_type ? ` (виправний №${d.doc_type})` : ''}`;
  const zero = !!fields.zero_zvit;
  const countryItems = (def.countries ?? []).map((c) => ({ id: c.code, label: c.name, hint: c.code }));
  const isOpt = formCode === 'S0601318';

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          <button onClick={() => navigate(`/stat-reports/${form}`)}>до списку</button>
          {!readOnly && (
            <>
              <button className="primary" onClick={() => save.mutate()} disabled={save.isPending || !dirty}>зберегти</button>
              <button onClick={runCheck} disabled={checking}>{checking ? '…' : 'перевірити'}</button>
              {isOpt && <button onClick={totals} disabled={zero} title="Заповнити підсумкові рядки 206, 299, 399 сумою частин (контролі «=»)">підсумки</button>}
              <button className="danger" disabled={!!subStatus && ['REJECTED'].includes(subStatus)}
                title={subStatus === 'REJECTED' ? 'Звіт уже надсилали до Держстату — видалити не можна' : undefined}
                onClick={() => { confirm('Видалити звіт?').then((ok) => { if (ok) remove.mutate(); }); }}>видалити</button>
            </>
          )}
          <a href={statReports.xmlUrl(d.id)} download title="XML звіту — для імпорту у Кабінет респондента Держстату або архіву">
            <button type="button">завантажити XML</button>
          </a>
        </div>
      </div>

      <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)', color: 'var(--badge-blue-fg)',
        padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span>
          {subStatus === 'ACCEPTED' ? '✓ прийнято Держстатом (квитанція №2)'
            : subStatus === 'REJECTED' ? '✗ не прийнято — виправте звіт і подайте виправний'
            : subStatus === 'DELIVERED' ? '✓ доставлено (квитанція №1), очікуємо квитанцію №2'
            : subStatus === 'SENT' ? '→ відправлено у шлюз Держстату — запитайте квитанції'
            : subStatus === 'ERROR' ? `помилка шлюзу: ${sub?.error_message ?? ''}`
            : subStatus === 'SIGNED' ? 'підписано — лишилося відправити'
            : 'чернетка — заповніть бланк, перевірте і підпишіть ключем'}
        </span>
        <a style={{ cursor: 'pointer', marginLeft: 'auto' }} onClick={() => navigate('/stat-cabinet')}>Кабінет респондента →</a>
      </div>

      <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 8, display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span>{d.document_number} · {d.form_code} · {org?.name ?? ''} · {FORM_TITLE[formCode]}</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Дата заповнення (D_FILL у файлі)">
          Дата
          <div style={{ width: 130 }}>
            <DatePopover value={d.document_date?.slice(0, 10) ?? ''} onChange={(v) => { if (v && !readOnly) saveDocDate.mutate(v); }} />
          </div>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Порядковий номер однотипного звіту в періоді (C_DOC_CNT)">
          № в періоді
          <input type="text" inputMode="numeric" value={docCnt} disabled={readOnly} style={{ width: 60 }}
            onChange={(e) => { setDocCnt(e.target.value.replace(/\D/g, '')); setDirty(true); }} />
        </label>
      </div>
      {error && <div style={{ color: 'var(--badge-red-fg)', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>}

      {check && (
        <div style={{ marginBottom: '0.75rem' }} data-testid="stat-check">
          {check.errors.length === 0 && (
            <div style={{ background: 'var(--badge-green-bg)', color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
              ✓ Помилок не знайдено — звіт готовий до подання{check.file_name ? ` (файл ${check.file_name})` : ''}
            </div>
          )}
          {check.errors.length > 0 && (
            <div style={{ background: 'var(--badge-red-bg)', color: 'var(--badge-red-fg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
              <b>Помилки ({check.errors.length}) — з ними Держстат звіт не прийме:</b>
              <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>{check.errors.map((e, i) => <li key={i} style={{ fontSize: '0.85rem' }}>{e.message}</li>)}</ul>
            </div>
          )}
          {check.warnings.length > 0 && (
            <div style={{ background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)', padding: '0.5rem 0.75rem', borderRadius: 6, marginTop: 6 }}>
              <b>Перевірте ({check.warnings.length}):</b>
              <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>{check.warnings.map((w, i) => <li key={i} style={{ fontSize: '0.85rem' }}>{w.message}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      {/* Реквізити бланка */}
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Керівник (власне імʼя ПРІЗВИЩЕ)</label>
          <input value={fields.ruk ?? ''} disabled={readOnly} onChange={(e) => setF({ ruk: e.target.value })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Телефон</label>
          <input value={fields.tel ?? ''} disabled={readOnly} onChange={(e) => setF({ tel: e.target.value })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Електронна пошта (на неї приходять квитанції)</label>
          <input value={fields.email ?? ''} disabled={readOnly} onChange={(e) => setF({ email: e.target.value })} />
        </div>
      </div>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>КАТОТТГ адреси здійснення діяльності (фактична адреса)</label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input value={fields.katottg_fact ?? ''} disabled={readOnly} style={{ flex: 1 }}
              onChange={(e) => setF({ katottg_fact: e.target.value.toUpperCase().replace(/[^UA0-9]/g, '') })} />
            {!readOnly && <KatottgPicker onPick={(code) => setF({ katottg_fact: code })} />}
          </div>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Фактична адреса (якщо відрізняється від юридичної)</label>
          <input value={fields.actual_address ?? ''} disabled={readOnly} placeholder={org?.legal_address ?? ''}
            onChange={(e) => setF({ actual_address: e.target.value })} />
        </div>
      </div>

      {/* Відсутність даних */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.6rem 0.75rem', margin: '0.5rem 0 0.75rem' }}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={zero} disabled={readOnly} onChange={(e) => setF({ zero_zvit: e.target.checked, reason_key: e.target.checked ? fields.reason_key ?? null : null })} />
          <b>Даних немає</b> <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>— показники не заповнюються, вказується причина</span>
        </label>
        {zero && (
          <div className="field" style={{ marginTop: 6 }}>
            <label>Причина відсутності даних</label>
            <select value={fields.reason_key ?? ''} disabled={readOnly} onChange={(e) => setF({ reason_key: e.target.value || null })}>
              <option value="">— оберіть —</option>
              {def.reasons.map((r) => (
                <option key={r.key} value={r.key} disabled={r.code === null}>
                  {r.code !== null ? `${r.code} — ` : ''}{r.label}{r.code === null ? ' (код не опубліковано — подається через Кабінет респондента)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {!zero && isOpt && (
        <>
          <div style={{ position: 'relative', margin: '0.25rem 0 0.5rem', maxWidth: 560 }}>
            <input value={goodsQ} onChange={(e) => setGoodsQ(e.target.value)} placeholder="Знайти рядок за товаром: назва або код НТВТ (напр. «шини», «46.32.11»)…" style={{ width: '100%' }} aria-label="Пошук рядка за товаром" />
            {goods.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: 'var(--bg-panel)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', maxHeight: 320, overflowY: 'auto', boxShadow: 'var(--shadow-lg)' }}>
                {goods.map((g) => (
                  <div key={g.code} style={{ padding: '0.4rem 0.6rem', borderTop: '1px solid var(--border-soft)', fontSize: '0.85rem', cursor: 'pointer' }}
                    onClick={() => g.rows[0] && jumpTo(g.rows[0])}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, marginRight: 6 }}>{g.code}</span>{g.matched}
                    <div style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
                      → рядок {g.rows.map((r, i) => `${r} «${g.row_labels[i]}», ${g.units[i]}`).join('; ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <TchTable widthsKey="stat-1opt">
            <thead>
              <tr>
                <th style={{ width: 54 }}>Код</th>
                <th>Найменування товарних груп (одиниця)</th>
                {[1, 2, 3, 4, 5, 6].map((c) => <th key={c} style={{ width: 118 }} title={def.columns?.[c - 1]?.label}>{c}. {COL_SHORT[c]}</th>)}
              </tr>
            </thead>
            <tbody>
              {(def.rows as OptRow[]).map((r) => (
                <tr key={r.code} ref={(el) => { rowRefs.current[r.code] = el; }}
                  style={{ background: highlight === r.code ? 'var(--badge-amber-bg)' : undefined, fontWeight: r.level === 0 ? 700 : 400 }}>
                  <td style={{ textAlign: 'center' }}>{r.code}</td>
                  <td style={{ paddingLeft: `${0.4 + r.level * 1.2}rem` }} title={r.note ?? undefined}>
                    {r.label}, {r.unit}{r.note ? <span style={{ color: 'var(--muted)', fontSize: '0.72rem' }}> ({r.note})</span> : null}
                  </td>
                  {[1, 2, 3, 4, 5, 6].map((c) => (
                    <td key={c} style={{ textAlign: 'right' }}>
                      {r.cols.includes(c)
                        ? <NumInput value={values[`A${r.code}_${c}`] ?? ''} disabled={readOnly} label={`ряд. ${r.code} гр. ${c}`}
                            title={r.integer && (c === 1 || c === 5) ? `${r.unit} — у цілих числах` : 'з одним десятковим знаком'}
                            onChange={(v) => setV(`A${r.code}_${c}`, v)} />
                        : <span style={{ color: 'var(--muted)' }}>Х</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </TchTable>
        </>
      )}

      {!zero && !isOpt && (
        <>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.25rem 0 0.5rem' }}>
            <b>Країни походження нерезидентів</b>
            <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>кожна країна — окрема графа; кілька інвесторів з однієї країни зводяться в одну графу</span>
            {!readOnly && <button style={{ marginLeft: 'auto' }} onClick={() => { setCountries((p) => [...p, { name: '', code: '', values: {} }]); setDirty(true); }}>+ країна</button>}
          </div>
          <TchTable widthsKey="stat-10zez">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Код</th>
                <th>Назва показника</th>
                {countries.map((c, i) => (
                  <th key={i} style={{ width: 200 }}>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <SearchSelect items={countryItems} value={c.code} disabled={readOnly} placeholder="— країна —"
                          onChange={(code) => setCountry(i, { code, name: def.countries?.find((x) => x.code === code)?.name ?? '' })} />
                      </div>
                      {!readOnly && <button title="прибрати країну" onClick={() => { setCountries((p) => p.filter((_, k) => k !== i)); setDirty(true); }}>×</button>}
                    </div>
                    <div style={{ fontWeight: 400, fontSize: '0.72rem', color: 'var(--muted)' }}>гр.{i + 1} · код {c.code || '—'}</div>
                  </th>
                ))}
                {countries.length === 0 && <th style={{ color: 'var(--muted)', fontWeight: 400 }}>додайте країну</th>}
              </tr>
            </thead>
            <tbody>
              {def.sections.map((s) => (
                <SectionRows key={s.key} section={s} rows={(def.rows as ZezRow[]).filter((r) => r.section === s.key)}
                  countries={countries} readOnly={readOnly} setCV={setCV} />
              ))}
            </tbody>
          </TchTable>

          <div style={{ marginTop: '0.75rem' }}>
            <b>Розділ III. Внески підприємства до статутного капіталу інших підприємств-резидентів (≥10%) станом на 31 грудня</b>
            <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}> — лише у звіті за IV квартал</span>
          </div>
          {d.period_quarter === 4 ? (
            <TchTable widthsKey="stat-10zez-s3">
              <thead>
                <tr><th style={{ width: 60 }}>Код</th><th>ЄДРПОУ іншого підприємства-резидента</th>
                  <th style={{ width: 160 }}>Частка участі, % (≥10 і ≤100)</th><th style={{ width: 160 }}>Внесок, тис.грн</th></tr>
              </thead>
              <tbody>
                {(def.section3_rows ?? []).map((r) => (
                  <tr key={r}>
                    <td style={{ textAlign: 'center' }}>{r}</td>
                    <td><input value={values[`A${r}_1`] ?? ''} disabled={readOnly} inputMode="numeric" maxLength={8} aria-label={`ряд. ${r} ЄДРПОУ`}
                      onChange={(e) => setV(`A${r}_1`, e.target.value.replace(/\D/g, ''))} style={{ width: '100%' }} /></td>
                    <td><NumInput value={values[`A${r}_2`] ?? ''} disabled={readOnly} label={`ряд. ${r} частка`} onChange={(v) => setV(`A${r}_2`, v)} /></td>
                    <td><NumInput value={values[`A${r}_3`] ?? ''} disabled={readOnly} label={`ряд. ${r} внесок`} onChange={(v) => setV(`A${r}_3`, v)} /></td>
                  </tr>
                ))}
                {(def.questions ?? []).map((q) => (
                  <tr key={q.code}>
                    <td style={{ textAlign: 'center' }}>{q.code}</td>
                    <td colSpan={1}>{q.label}</td>
                    <td colSpan={2}>
                      <label style={{ marginRight: 12 }}><input type="radio" name={`q${q.code}`} disabled={readOnly} checked={values[`A${q.code}`] === 'yes'} onChange={() => setV(`A${q.code}`, 'yes')} /> так</label>
                      <label><input type="radio" name={`q${q.code}`} disabled={readOnly} checked={values[`A${q.code}`] === 'no'} onChange={() => setV(`A${q.code}`, 'no')} /> ні</label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TchTable>
          ) : null}
        </>
      )}

      <StatSignPanel organizationId={d.organization_id} reportId={d.id} getFiles={getFiles} sub={sub}
        onSubmitted={() => invalidate()} onReceipts={() => invalidate()} />
    </div>
  );
}

function SectionRows({ section, rows, countries, readOnly, setCV }: {
  section: { key: string; label: string; unit?: string }; rows: ZezRow[]; countries: StatCountryBlock[]; readOnly: boolean;
  setCV: (i: number, row: string, raw: string) => void;
}) {
  const span = 2 + Math.max(countries.length, 1);
  return (
    <>
      <tr><td colSpan={span} style={{ fontWeight: 700, background: 'var(--bg-soft)' }}>{section.label} <span style={{ fontWeight: 400, color: 'var(--muted)' }}>({section.unit})</span></td></tr>
      {rows.map((r) => (
        <tr key={r.code} style={{ fontWeight: r.bold ? 700 : 400 }}>
          <td style={{ textAlign: 'center' }}>{r.code}</td>
          <td style={{ paddingLeft: `${0.4 + (r.level ?? 0) * 1.2}rem` }}>
            {r.label}{r.formula ? <span style={{ color: 'var(--muted)', fontSize: '0.72rem' }}> (= {r.formula})</span> : null}
          </td>
          {countries.map((c, i) => (
            <td key={i}>
              <NumInput value={c.values[r.code] ?? ''} disabled={readOnly} label={`ряд. ${r.code} гр. ${i + 1}`}
                title={r.signed ? 'з урахуванням знаку (може бути відʼємним)' : 'з одним десятковим знаком'}
                onChange={(v) => setCV(i, r.code, v)} />
            </td>
          ))}
          {countries.length === 0 && <td />}
        </tr>
      ))}
    </>
  );
}
